import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

const DEFAULT_PATH = 'default';
const WINBACK_PATH = 'winback';

export default function useLocalStorage(path: string = DEFAULT_PATH) {
    const location = useLocation();
    const isWinback = useMemo(() => location.pathname.includes('lovely-user/winback'), [location.pathname]);

    const storagePath = isWinback ? WINBACK_PATH : path;

    const read = (key: string, defaultValue?: unknown) => {
        const storageKey = `${storagePath}.${key}`;
        const item = localStorage.getItem(storageKey);
        const parsedItem = item ? JSON.parse(item) : undefined;
        const result = parsedItem || defaultValue;
        return result;
    };

    const write = (key: string, value: unknown) => {
        const storageKey = `${storagePath}.${key}`;
        localStorage.setItem(storageKey, JSON.stringify(value));
    };

    const remove = (key: string) => {
        const storageKey = `${storagePath}.${key}`;
        localStorage.removeItem(storageKey);
    };

    const storage = {
        read,
        write,
        remove,
    };

    return storage;
}

// Earlier versions saved the whole step 1 form, password included, under
// authStepData. Strip the password from what is already in visitors' browsers;
// the rest stays, so an unfinished signup resumes where it was left.
export function removeStoredSignupPassword() {
    [DEFAULT_PATH, WINBACK_PATH].forEach((path) => {
        const storageKey = `${path}.authStepData`;
        try {
            const item = localStorage.getItem(storageKey);
            const data = item ? JSON.parse(item) : undefined;
            if (data && typeof data === 'object' && 'password' in data) {
                delete data.password;
                localStorage.setItem(storageKey, JSON.stringify(data));
            }
        } catch {
            // Storage blocked or the value is not JSON: nothing of ours to clean.
        }
    });
}

// The site's email field opens the sign-up with ?email=. Everything on the page reads the address — PostHog keeps the
// first one a year in a cookie every subdomain of tapfiliate.com can read, the marketing tags send it on — so before
// any of them starts, the email moves into the step 1 draft the form prefills from, and leaves the address. With
// storage blocked it stays in the address, where the form finds it too.
export function moveEmailFromAddressToDraft() {
    const address = new URL(window.location.href);
    const email = address.searchParams.get('email');
    if (email === null) return;
    const path = address.pathname.includes('lovely-user/winback') ? WINBACK_PATH : DEFAULT_PATH;
    const storageKey = `${path}.authStepData`;
    try {
        const item = localStorage.getItem(storageKey);
        const data = item ? JSON.parse(item) : undefined;
        const draft = data && typeof data === 'object' ? data : {};
        localStorage.setItem(storageKey, JSON.stringify({ ...draft, email: email.toLowerCase() }));
    } catch {
        return;
    }
    address.searchParams.delete('email');
    window.history.replaceState(window.history.state, '', address.href);
}
